import { arrivalClock } from '../components/ArrivalInput';
import { useState } from 'react';
import { api } from '../lib/api';
import { dash, money, useList, wallClock, type Row } from '../components/DataTable';
import { usePhone } from '../lib/phone';
import { useSticky } from '../lib/sticky';

/**
 * 한 접수의 **노선** 목록. 접수 화면 오른쪽 패널의 가운데 칸이다.
 *
 * 왜 이 자리인가: 접수 목록은 `노선수` 를 숫자로만 보여줬다. 그 노선이 무엇인지 보려면
 * 노선 탭으로 옮겨 거래처명으로 **다시 검색**해야 했다. 이제 왼쪽에서 접수를 고르면
 * 여기에 노선이 서고, 노선을 고르면 아래에 배차된 차량이 선다 — 세 계층이 한 화면이다.
 *
 * ⚠️ **컬럼을 줄였다.** 배차 판단에 필요한 것만 둔다(상태·노선명·출발·출발지/도착지·
 * 배정/필요·청구액·운행료·정산월). 차종·편도왕복은 **노선 탭**에 그대로 있고,
 * 아래 "노선 탭에서 이 접수만 보기" 가 그 전체 표로 데려간다. 감춘 게 아니라 자리를 나눴다.
 *
 * ⚠️ **200건 상한이 업무 상한이 아니다.** 서버 `paging()` 이 limit 을 200 으로 자르고,
 * 실데이터에는 노선이 **385건인 일반 접수**(통근은 659건)가 있다. 잘렸으면 조용히 넘기지 않고
 * 몇 건 중 몇 건인지 적는다. 총 건수는 접수 행이 이미 들고 있는 `dispt_cnt` 다 — 따로 세지 않는다.
 */
const CAP = 200;

/** KST 벽시계 'YYYY-MM-DD HH:MM:SS' — end_time 과 문자열로 견준다(서버가 KST 벽시계 문자열로 준다). */
const kstNow = () => new Date(Date.now() + 9 * 3600_000).toISOString().slice(0, 19).replace('T', ' ');

export default function RecvRoutes({
  recv,
  selectedDisptId,
  onPick,
  onSeeAll,
  nonce,
}: {
  recv: Row;
  selectedDisptId: number | null;
  /** 노선 줄을 눌렀을 때 — 아래 칸에 그 노선의 배차 차량이 선다. */
  onPick: (dispt: Row | null) => void;
  onSeeAll: (recvId: number) => void;
  /** 노선을 저장·삭제한 뒤 다시 읽게 하는 값. */
  nonce: number;
}) {
  const recvId = Number(recv.recv_id);
  // 폰(2026-09-30 way 「배차 표도」): NO·구간 칸은 빼고 노선명을 왼쪽에 고정한다.
  const phone = usePhone();
  const pHide = phone ? 'hidden' : '';
  const pin = phone ? 'sticky left-0 z-[5] max-w-[40vw] bg-inherit shadow-[1px_0_0_rgb(229_231_235)]' : '';
  const total = Number(recv.dispt_cnt ?? 0);

  const { rows, loading, error } = useList(
    () => api.listDispts({ recvId, limit: CAP }) as unknown as Promise<{ rows: Row[] }>,
    [recvId, nonce],
  );

  // 🔴 끝난 노선 접기(#24) — 통근처럼 수년 이어지는 접수엔 운행이 끝난 노선(도착일이 지난 것)이 지금 노선과
  // 섞여 서서 「지금 운행 중인 노선」을 오독한다(한화63시티 순환셔틀5 — 2024-07 종료). 지금 운행 중인 노선이
  // **하나라도 있을 때만** 끝난 것을 접는다. 전부 끝난 접수(지난 일반 배차)는 접을 게 없으니 그대로 다 보인다.
  const [showEnded, setShowEnded] = useState(false);
  const now = kstNow();
  const ended = (r: Row) => typeof r.end_time === 'string' && r.end_time !== '' && r.end_time < now;
  const endedCnt = rows.filter(ended).length;
  const folding = endedCnt > 0 && endedCnt < rows.length && !showEnded;
  // #173 출발 순서 — 기본 이른 날짜부터(오름차순). 「출발」 머리글을 누르면 뒤집는다(계정에 기억).
  const [startAsc, setStartAsc] = useSticky('dispatch.routes.start-asc', true, { persist: true });
  const byStart = (a: Row, b: Row) => {
    const x = String(a.start_time ?? ''); const y = String(b.start_time ?? '');
    if (!x || !y) return x ? -1 : y ? 1 : Number(a.dispt_id) - Number(b.dispt_id); // 출발 없는 노선은 늘 맨 뒤
    return (x < y ? -1 : x > y ? 1 : Number(a.dispt_id) - Number(b.dispt_id)) * (startAsc ? 1 : -1);
  };
  const visible = (folding ? rows.filter((r) => !ended(r) || selectedDisptId === Number(r.dispt_id)) : rows).slice().sort(byStart);

  return (
    <div className="card p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-ink-soft">
        <b className="text-sm text-ink">노선 {loading ? '' : visible.length}</b>
        {/* 전체 노선 청구·지급 합계(2026-09-30 way) — 청구 = 노선 총액 합, 운행료 = 단가 × 대수(cust_amt·driver_amt, 서버가 계산). 보이는 노선 기준. */}
        {!loading && visible.length > 0 && (() => {
          const cust = visible.reduce((a, r) => a + Number(r.cust_amt ?? 0), 0);
          const drv = visible.reduce((a, r) => a + Number(r.driver_amt ?? 0), 0);
          return (
            <span className="rounded bg-canvas px-2 py-0.5 text-sm text-ink">
              청구 합계 <b className="tabular-nums">{money(cust)}</b>
              <span className="mx-2 text-ink-faint">·</span>
              운행료 합계 <b className="tabular-nums">{money(drv)}</b>
              <span className="mx-2 text-ink-faint">·</span>
              차익 <b className={`tabular-nums ${cust - drv < 0 ? 'text-red-700' : ''}`}>{money(cust - drv)}</b>
            </span>
          );
        })()}
        {!loading && endedCnt > 0 && endedCnt < rows.length && (
          <button className="rounded bg-canvas px-1.5 py-0.5 underline" onClick={() => setShowEnded((v) => !v)}>
            {showEnded ? '운행 끝난 노선 접기' : `운행 끝난 노선 ${endedCnt}개 보기`}
          </button>
        )}
        {!loading && Number.isFinite(total) && total > rows.length && (
          <span className="rounded bg-amber-50 px-1.5 py-0.5 font-medium text-amber-700">
            전체 {total}건 중 {rows.length}건만 표시
          </span>
        )}
        <span>줄을 누르면 그 노선에 배차된 차량이 아래에 열립니다.</span>
        <button className="ml-auto text-xs underline" onClick={() => onSeeAll(recvId)}>
          노선 탭에서 이 접수만 보기
        </button>
      </div>

      {error && <div className="text-xs text-red-700">{error}</div>}

      {loading ? (
        <div className="text-xs text-ink-faint">불러오는 중…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-ink-faint">
          노선이 없습니다. 접수만 있고 운행이 아직 안 붙은 상태입니다 — 위 “노선 추가”로 붙입니다.
        </div>
      ) : (
        // 높이를 묶고 **목록 안에서만** 세로로 스크롤한다. 안 묶으면 노선이 많은 접수에서
        // 목록이 그대로 늘어나 **그 아래 배차 칸이 화면 밖으로 밀려난다**(노선 200줄까지 온다).
        // 짧으면 상한이 하는 일이 없으므로 노선 1개짜리(전체의 70%)는 그대로다.
        <div className="max-h-[22rem] overflow-auto">
          <table className="grid-lines w-full">
            {/* 스크롤해도 머리글은 남는다 — 안 그러면 중간에서 어느 칸인지 알 수 없다. */}
            <thead className="sticky top-0 z-10">
              <tr>
                {['NO', '상태', '노선명', '출발', '정산월', '구간', '배정/필요', '청구액', '운행료 합계'].map((h) => (
                  <th
                    key={h}
                    className={`th bg-white ${h === 'NO' || h === '배정/필요' || h === '청구액' || h === '운행료 합계' ? 'text-right' : ''} ${
                      h === 'NO' || h === '구간' ? pHide : h === '노선명' && phone ? 'sticky left-0 z-20' : ''}`}
                  >
                    {h === '출발' ? (
                      <button type="button" className="hover:text-brand-600" title="눌러서 출발 순서 바꾸기"
                              onClick={() => setStartAsc(!startAsc)}>
                        출발 {startAsc ? '▲' : '▼'}
                      </button>
                    ) : h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => {
                const on = selectedDisptId === Number(r.dispt_id);
                return (
                  <tr
                    key={String(r.dispt_id)}
                    onClick={() => onPick(on ? null : r)}
                    className={`cursor-pointer ${on ? 'bg-brand-50' : 'bg-white hover:bg-canvas'} ${ended(r) ? 'text-ink-faint' : ''}`}
                    title={ended(r) ? `운행 끝난 노선 — 도착 ${arrivalClock(r.end_time)}` : undefined}
                  >
                    {/* 노선(배차) NO = 레거시 DisptId 와 같은 번호. */}
                    <td className={`td num text-xs text-ink-soft ${pHide}`}>{dash(r.dispt_id)}</td>
                    <td className="td">{dash(r.dispt_status)}</td>
                    {/* 이름·지명은 길다. 잘라내지 말고 **어절 단위로 줄바꿈**한다(한국어 기본값은 어절을 쪼갠다). */}
                    <td className={`td max-w-[14rem] whitespace-normal break-keep ${pin}`}>{dash(r.dispt_name)}</td>
                    <td className="td">{wallClock(r.start_time)}</td>
                    {/* 정산월(10-29 way) — 일반 배차는 이 달로 거래처 정산에 들어간다. 여러 노선이 어느 달로 잡혔는지 한눈에. */}
                    <td className="td whitespace-nowrap">{dash(r.drive_mon)}</td>
                    {/* 출발지·도착지를 한 칸으로 — 두 칸이면 8칸이 되어 이 폭에서 오른쪽 금액이 잘린다. */}
                    <td className={`td max-w-[16rem] whitespace-normal break-keep ${pHide}`}>
                      {dash(r.start_loc)}
                      <span className="mx-1 text-ink-faint">→</span>
                      {dash(r.end_loc)}
                    </td>
                    <td className="td num">
                      {dash(r.assigned_cnt)} / {dash(r.car_cnt)}
                    </td>
                    {/* 청구액은 **노선 총액**이라 대수를 곱하지 않는다(issues 179 — 청구 집계와 같은 값).
                        운행료는 1대당 단가라 합계(단가 × 대수)를 크게, 단가 × 대수를 작게 같이 보여준다(issues 84). */}
                    <td className="td num">{money(r.cost_cust)}</td>
                    <td className="td num">
                      {money(r.driver_amt ?? r.cost_driver)}
                      {Number(r.car_cnt) > 1 && (
                        <div className="text-xs text-ink-faint">
                          {money(r.cost_driver)} ×{dash(r.car_cnt)}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
